import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faShoppingCart,
  faExclamationTriangle,
  faFileInvoiceDollar,
  faTools,
  faUserCog,
  faTruck,
  faCogs,
  faReceipt,
  faUserCircle,
  faPhone,
  faMoneyBillWave,
  faBars,
  faMoneyCheckDollar,
  faFileInvoice,
  faTags,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";


// ---- Helper to fix duplicate icons ----
const fixDuplicateIcons = (items) => {
  const seen = new Set();
  const alternates = [
    faMoneyCheckDollar,
    faFileInvoice,
    faTags,
    faUserTie,
    // add more alternates here if needed
  ];
  let altIndex = 0;

  return items.map(item => {
    const iconKey = item.icon?.iconName || "";
    if (seen.has(iconKey)) {
      const newIcon = alternates[altIndex % alternates.length];
      altIndex++;
      return { ...item, icon: newIcon };
    } else {
      seen.add(iconKey);
      return item;
    }
  });
};


// ---- BOTTOM NAV ITEM ----
const BottomNavItem = ({
  icon,
  text,
  list = [],
  activeItem,
  setActiveItem,
  onClick,
  path,
  isExpandable = false,
  expanded,
  setExpanded,
  allServicesRef,
}) => {
  const isActive =
    activeItem === text || list.some((item) => activeItem === item.name);

const handleClick = (e) => {
  if (isExpandable) {
    e.preventDefault();
    if (expanded) {
      // ✅ If already open → close only
      setExpanded(false);
    } else {
      // ✅ If closed → open it and set active
      setExpanded(true);
      setActiveItem(text);
    }
    if (onClick) onClick();
  } else {
    // ✅ Normal items just set active
    setActiveItem(text);
    if (onClick) onClick();
  }
};


  // ✅ If expandable (All Services) → use <div>
  if (isExpandable) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center py-2 cursor-pointer select-none relative"
        onClick={handleClick}
      >
        <span
          className={`flex items-center justify-center h-7 w-7 rounded-full ${
            isActive ? " text-[#0CBB7D]" : "text-gray-500"
          } transition-colors duration-200`}
        >
          <FontAwesomeIcon icon={icon} className="text-lg" />
        </span>
        <span
          className={`text-xs mt-1 transition-colors font-semibold ${
            isActive ? "text-[#0CBB7D]" : "text-gray-500"
          }`}
        >
          {text}
        </span>

        {/* Dropdown */}
        <div
          ref={allServicesRef}
          className={`fixed bottom-[64px] left-0 w-screen bg-white border-t border-gray-200 shadow-md z-50 p-4 transform transition-all duration-500 ease-in-out ${
            expanded
              ? "opacity-100 translate-y-0 max-h-[60vh]"
              : "opacity-0 translate-y-5 max-h-0 pointer-events-none"
          }`}
          style={{ overflowY: expanded ? "auto" : "hidden" }}
        >
          <div className="grid grid-cols-4 gap-4">
            {list.map((subItem, idx) => (
              <Link
                key={idx}
                to={subItem.path}
                onClick={() => {
                  setActiveItem(subItem.name);
                  setExpanded(false);
                }}
                className="flex flex-col items-center cursor-pointer hover:bg-gray-100 rounded-md p-2"
              >
                {subItem.icon && (
                  <span className="text-[#0CBB7D] text-xl mb-1 flex justify-center w-6 h-6">
                    <FontAwesomeIcon icon={subItem.icon} />
                  </span>
                )}
                <span className="text-[12px] font-semibold text-gray-700 text-center">
                  {subItem.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ✅ If not expandable (Home, Orders, etc.) → use <Link>
  return (
    <Link
      to={path}
      className="flex-1 flex flex-col items-center justify-center py-2 cursor-pointer select-none relative"
      onClick={handleClick}
    >
      <span
        className={`flex items-center justify-center h-7 w-7 rounded-full ${
          isActive ? " text-[#0CBB7D]" : "text-gray-500"
        } transition-colors duration-200`}
      >
        <FontAwesomeIcon icon={icon} className="text-lg" />
      </span>
      <span
        className={`text-xs mt-1 transition-colors font-semibold ${
          isActive ? "text-[#0CBB7D]" : "text-gray-500"
        }`}
      >
        {text}
      </span>
    </Link>
  );
};




// ---- MAPS (no change) ----
const accessMap = {
  NDR: "ndr",
  Tools: "tools",
  Finance: "finance",
  "Setup & Manage": "setupAndManage",
  Courier: "courier",
  Order: "orders",
  Orders: "orders",
  Support: "support",
  Billing: "finance",
  "Assign Plan": "courier",
};


// ---- MAIN COMPONENT ----
const Sidebar = ({ isAdmin: isAdminProp, adminTab: adminTabProp }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminTab, setIsAdminTab] = useState();
  const [searchParams] = useSearchParams();
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const allServicesRef = useRef(null);

const [activeItem, setActiveItem] = useState(() => {
  const stored = localStorage.getItem("activeSidebarItem");
  if (stored) {
    if (!isAdmin && stored === "Dashboard") return "Home"; // normalize
    return stored;
  }
  return isAdmin ? "Dashboard" : "Home";
});


useEffect(() => {
  const valueToStore = !isAdmin && activeItem === "Dashboard" ? "Home" : activeItem;
  localStorage.setItem("activeSidebarItem", valueToStore);
}, [activeItem, isAdmin]);


  // Employee Access
  const [employeeAccessRights, setEmployeeAccessRights] = useState(null);
  const [isEmployee, setIsEmployee] = useState(false);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (typeof isAdminProp === "boolean") setIsAdmin(isAdminProp);
    else if (searchParams.get("isAdmin") === "true") setIsAdmin(true);
    else setIsAdmin(false);

    if (typeof adminTabProp === "boolean") setIsAdminTab(adminTabProp);
    else if (searchParams.get("adminTab") === "true") setIsAdminTab(true);
    else setIsAdminTab(false);

    const fetchRoleAccess = async () => {
      try {
        const [session, token] = document.cookie.split("=");
        const res = await axios.get(
          `${REACT_APP_BACKEND_URL}/staffRole/verify`,
          { headers: { authorization: `Bearer ${token}` } }
        );
        console.log("res.data", res.data);
        if (res.data?.employee) {
          setIsEmployee(true);
          setEmployeeAccessRights(res.data.employee.accessRights || {});
        } else {
          setIsEmployee(false);
          setEmployeeAccessRights(null);
        }
      } catch {
        setIsEmployee(false);
        setEmployeeAccessRights(null);
      }
    };
    fetchRoleAccess();
  }, [isAdminProp, adminTabProp, searchParams, REACT_APP_BACKEND_URL, location.pathname]);

  useEffect(() => {
    if (adminTab === undefined) return;
    if (isAdmin && adminTab && (location.pathname === "/" || location.pathname === "/dashboard")) {
      navigate("/adminDashboard", { replace: true });
    } else if ((!isAdmin || !adminTab) && (location.pathname === "/" || location.pathname === "/adminDashboard")) {
      navigate("/dashboard", { replace: true });
    }
  }, [adminTab, isAdmin, location.pathname, navigate]);

  // Sidebar items base
  const sidebarItems = [
    { icon: faHome, text: "Dashboard", path: "/" },
    { icon: faShoppingCart, text: "Order", path: "/dashboard/order" },
    { icon: faShoppingCart, text: "Orders", path: "/adminDashboard/order" },
    { icon: faExclamationTriangle, text: "NDR", path: "/dashboard/ndr" },
    { icon: faExclamationTriangle, text: "NDR", path: "/adminDashboard/ndr" },
    { icon: faFileInvoiceDollar, text: "Billing", path: "/dashboard/billing" },
    {
      icon: faMoneyBillWave,
      text: "Finance",
      extent: true,
      list: [
        { name: "COD", path: "/finance/COD" },
        { name: "Billing", path: "/finance/billing" },
      ],
    },
    {
      icon: faTools,
      text: "Tools",
      extent: true,
      list: [
        { name: "Rate Calculator", path: "/dashboard/tools/Cost_Estimation" },
        { name: "Weight Discrepancy", path: "/dashboard/tools/Weight_Dependency" },
        { name: "Weight Discrepancy", path: "/dashboard/tools/admin_Weight_Dependency" },
      ],
    },
    {
      icon: faUserCog,
      text: "Setup & Manage",
      extent: true,
      list: [
        { name: "Pickup Address", path: "/dashboard/Setup&Manage/Pickup_address" },
        { name: "Users", path: "/dashboard/user" },
        { name: "Channels", path: "/dashboard/Setup&Manage/Channel" },
        { name: "Roles", path: "/dashboard/Setup&Manage/Role_List" },
        { name: "Allocate Sellers", path: "/dashboard/Setup&Manage/allocateRoles" },
      ],
    },
    {
      icon: faTruck,
      text: "Courier",
      extent: true,
      list: [
        { name: "Couriers", path: "/dashboard/setup/courier/add" },
      ],
    },
    { icon: faReceipt, text: "Assign Plan", path: "/dashboard/assignPlan" },
    { icon: faCogs, text: "Settings", path: "/dashboard/settings" },
    { icon: faPhone, text: "Support", path: "/dashboard/support" },
  ];

  // Filtering logic unchanged
  const filteredSidebarItems = useMemo(() => {
    // --- EMPLOYEE CASE ---
    if (isEmployee && employeeAccessRights) {
      return sidebarItems
        .map((item) => {
          if (
            (item.text === "Order" && item.path === "/dashboard/order") ||
            (item.text === "Orders" && item.path === "/adminDashboard/order")
          ) {
            const section = employeeAccessRights["orders"];
            if (!section) return null;
            const anyView = Object.values(section).some(
              (v) => v && typeof v === "object" && v.view === true
            );
            if (anyView || section.view === true) {
              return {
                ...item,
                text: "Orders",
                path: "/adminDashboard/order",
              };
            }
            return null;
          }

          if (
            (item.text === "NDR" && item.path === "/dashboard/ndr") ||
            (item.text === "NDR" && item.path === "/adminDashboard/ndr")
          ) {
            const section = employeeAccessRights["ndr"];
            if (!section) return null;
            const anyView = Object.values(section).some(
              (v) => v && typeof v === "object" && v.view === true
            );
            if (anyView || section.view === true) {
              return { ...item, path: "/adminDashboard/ndr" };
            }
            return null;
          }

          if (
            (item.text === "Order" && item.path === "/dashboard/order") ||
            (item.text === "Orders" && item.path === "/adminDashboard/order") ||
            (item.text === "NDR" && item.path === "/dashboard/ndr") ||
            (item.text === "NDR" && item.path === "/adminDashboard/ndr")
          ) {
            return null;
          }

          if (
            (item.path && item.path.startsWith("/adminDashboard")) ||
            (item.list &&
              item.list.some(
                (sub) => sub.path && sub.path.startsWith("/adminDashboard")
              ))
          ) {
            return null;
          }

          if (item.text === "Dashboard") return item;

          const sectionKey = accessMap[item.text];
          const section = employeeAccessRights[sectionKey];
          if (item.extent && Array.isArray(item.list)) {
            const filteredList = item.list.filter((subItem) => {
              if (!section) return false;
              const subAccess = section[subItem.name];
              return subAccess && subAccess.view === true;
            });
            return filteredList.length > 0 ? { ...item, list: filteredList } : null;
          }
          if (!section) return null;
          if (typeof section === "object" && !Array.isArray(section)) {
            const anyView = Object.values(section).some(
              (v) => v && typeof v === "object" && v.view === true
            );
            return anyView ? item : null;
          }
          return section.view === true ? item : null;
        })
        .filter(Boolean)
        .filter((item, idx, arr) => {
          if (item.text === "Orders" && item.path === "/adminDashboard/order") {
            return (
              arr.findIndex(
                (i) => i.text === "Orders" && i.path === "/adminDashboard/order"
              ) === idx
            );
          }
          if (item.text === "NDR" && item.path === "/adminDashboard/ndr") {
            return (
              arr.findIndex(
                (i) => i.text === "NDR" && i.path === "/adminDashboard/ndr"
              ) === idx
            );
          }
          return true;
        });
    }

    // --- ADMIN CASE ---
    if (isAdmin) {
      return sidebarItems
        .map((item) => {
          if (item.text === "Dashboard") {
            if (isAdmin && adminTab) {
              return { ...item, path: "/adminDashboard" };
            } else {
              return { ...item, path: "/" };
            }
          }
          if (isAdmin && adminTab) {
            if (
              item.text === "Courier" ||
              item.text === "Assign Plan" ||
              item.text === "Finance" ||
              item.text === "Orders" ||
              item.path === "/adminDashboard/order" ||
              item.path === "/adminDashboard/ndr"
            ) {
              return item;
            }
            if (item.text === "Tools") {
              const filteredList = item.list.filter((subItem) =>
                ["/dashboard/tools/admin_Weight_Dependency"].includes(
                  subItem.path
                )
              );
              return filteredList.length > 0
                ? { ...item, list: filteredList }
                : null;
            }
            if (item.text === "Setup & Manage") {
              const filteredList = item.list.filter(
                (subItem) =>
                  ["Users", "Roles"].includes(subItem.name) ||
                  (subItem.name === "Allocate Sellers" &&
                    isAdmin === true &&
                    adminTab === true)
              );
              return filteredList.length > 0
                ? { ...item, list: filteredList }
                : null;
            }
            if (["Settings", "Support"].includes(item.text)) {
              return item;
            }
            return null;
          }
          if (!adminTab) {
            if (
              item.text === "Courier" ||
              item.text === "Assign Plan" ||
              item.text === "Finance" ||
              item.text === "Orders" ||
              item.path === "/adminDashboard/order" ||
              item.path === "/adminDashboard/ndr"
            ) {
              return null;
            }
            if (item.text === "Tools") {
              const filteredList = item.list.filter(
                (subItem) =>
                  subItem.path !== "/dashboard/tools/admin_Weight_Dependency"
              );
              return filteredList.length > 0
                ? { ...item, list: filteredList }
                : null;
            }
            if (item.text === "Setup & Manage") {
              const filteredList = item.list.filter(
                (subItem) =>
                  subItem.name !== "Users" &&
                  subItem.name !== "Roles" &&
                  subItem.name !== "Allocate Sellers"
              );
              return filteredList.length > 0
                ? { ...item, list: filteredList }
                : null;
            }
            return item;
          }
          return null;
        })
        .filter(Boolean);
    }

    // --- USER CASE (non-admin, non-employee) ---
    if (!isAdmin && !isEmployee) {
      return sidebarItems
        .filter((item) => !item.path || !item.path.startsWith("/adminDashboard"))
        .map((item) => {
          if (item.text === "Dashboard") {
            return { ...item, text: "Home" }; // ✅ rename here
          }
          if (item.extent && Array.isArray(item.list)) {
            let filteredList = item.list.filter(
              (subItem) =>
                !subItem.path || !subItem.path.startsWith("/adminDashboard")
            );
            if (item.text === "Setup & Manage") {
              filteredList = filteredList.filter(
                (subItem) =>
                  !["Users", "Roles", "Allocate Sellers"].includes(subItem.name)
              );
            }
            if (item.text === "Tools") {
              filteredList = filteredList.filter(
                (subItem) =>
                  subItem.path !== "/dashboard/tools/admin_Weight_Dependency"
              );
            }
            if (item.text === "Finance") {
              return null;
            }
            if (item.text === "Courier") {
              return null;
            }
            if (item.text === "Assign Plan") {
              return null;
            }
            return filteredList.length > 0 ? { ...item, list: filteredList } : null;
          }
          if (item.text === "Assign Plan") return null;
          return item;
        })
        .filter(Boolean);
    }

    return [];
  }, [isAdmin, adminTab, isEmployee, employeeAccessRights, sidebarItems]);



  // Separate items always outside All Services
  const separateItemsSet = new Set(["Dashboard", "Order", "Orders", "Support", "Settings"]);

  // Build all services list with icons, fixed duplicates
  const allServicesList = useMemo(() => {
    const itemsForAllServicesRaw = filteredSidebarItems.filter(
      (item) => !separateItemsSet.has(item.text)
    );

    const flattened = [];
    itemsForAllServicesRaw.forEach(item => {
      if (item.extent && Array.isArray(item.list)) {
        item.list.forEach(sub => {
          flattened.push({
            name: sub.name,
            path: sub.path,
            icon: item.icon,
          });
        });
      } else {
        flattened.push({
          name: item.text,
          path: item.path,
          icon: item.icon,
        });
      }
    });
    return fixDuplicateIcons(flattened);
  }, [filteredSidebarItems]);

  // Close All Services dropdown on outside click
  const [allServicesExpanded, setAllServicesExpanded] = useState(false);
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        allServicesExpanded &&
        allServicesRef.current &&
        !allServicesRef.current.contains(event.target)
      ) {
        setAllServicesExpanded(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [allServicesExpanded]);

  // Separate sidebar items
  const separateSidebarItems = useMemo(() => {
    return filteredSidebarItems.filter(item => separateItemsSet.has(item.text));
  }, [filteredSidebarItems]);

// Final items list with user/admin Dashboard/Home switch
const finalSidebarItems = useMemo(() => {
  if (allServicesList.length === 0) {
    return separateSidebarItems.map((item) => {
      if (item.text === "Dashboard" || item.text === "Home") {
        return {
          ...item,
          text: adminTab ? "Dashboard" : "Home",  // ✅ controlled by adminTab
        };
      }
      return item;
    });
  }

  const allServicesItem = {
    icon: faBars,
    text: "All Services",
    extent: true,
    list: allServicesList,
    path: "#",
  };

  const dashboardItem = separateSidebarItems.find(
    (i) => i.text === "Dashboard" || i.text === "Home"
  );

  const dashboardModified = dashboardItem
    ? {
        ...dashboardItem,
        text: adminTab ? "Dashboard" : "Home",  // ✅ adminTab decides
      }
    : null;

  return [
    dashboardModified,
    separateSidebarItems.find((i) => i.text === "Order"),
    separateSidebarItems.find((i) => i.text === "Orders"),
    allServicesItem,
    separateSidebarItems.find((i) => i.text === "Support"),
    separateSidebarItems.find((i) => i.text === "Settings"),
  ].filter(Boolean);
}, [separateSidebarItems, allServicesList, adminTab]);


  // Responsive check (optional, same style on desktop and mobile)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Render nav
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white shadow-inner border-t border-gray-200 z-50 flex"
      style={{
        height: "64px",
        width: "100vw",
        maxWidth: "100vw",
        minWidth: "100vw",
        overflow: "hidden",
      }}
    >
      {finalSidebarItems.map((item, index) =>
        item.extent && Array.isArray(item.list) && item.list.length > 0 ? (
          item.text === "All Services" ? (
            <BottomNavItem
              key={index}
              icon={item.icon}
              text={item.text}
              list={item.list}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              isExpandable={true}
              expanded={allServicesExpanded}
              setExpanded={setAllServicesExpanded}
              allServicesRef={allServicesRef}
            />
          ) : (
            <BottomNavItem
              key={index}
              icon={item.icon}
              text={item.list[0].name}
              path={item.list[0].path}
              list={item.list}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
            />
          )
        ) : (
         <BottomNavItem
  key={index}
  icon={item.icon}
  text={item.text}  // ✅ trust finalSidebarItems rename
  path={item.path}
  activeItem={activeItem}
  setActiveItem={setActiveItem}
/>

        )
      )}
    </nav>
  );
};

export default Sidebar;
